import { useEffect } from 'react';

interface WebVitalsMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

interface WebVitalsThresholds {
  LCP: { good: number; poor: number };
  FID: { good: number; poor: number };
  CLS: { good: number; poor: number };
  FCP: { good: number; poor: number };
  TTFB: { good: number; poor: number };
  INP: { good: number; poor: number };
}

const THRESHOLDS: WebVitalsThresholds = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 }
};

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = THRESHOLDS[name as keyof WebVitalsThresholds];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

function sendToAnalytics(metric: WebVitalsMetric) {
  // Microsoft Clarity'ye gönder
  if (typeof window !== 'undefined' && (window as any).clarity) {
    (window as any).clarity('event', 'web_vitals', {
      metric_name: metric.name,
      metric_value: metric.value,
      metric_rating: metric.rating,
      metric_id: metric.id
    });
  }

  // Console'da development için log
  if (process.env.NODE_ENV === 'development') {
    console.log(`🎯 Web Vital: ${metric.name}`, {
      value: `${metric.value}ms`,
      rating: getRating(metric.name, metric.value),
      threshold: THRESHOLDS[metric.name as keyof WebVitalsThresholds]
    });
  }

  // Performance budget kontrolü
  if (metric.rating === 'poor') {
    console.warn(`⚠️ Performance Budget Exceeded: ${metric.name} = ${metric.value}ms`);
  }
}

export function useWebVitals() {
  useEffect(() => {
    // Web Vitals library'yi dinamik olarak yükle
    if (typeof window === 'undefined') return;
    
    import('web-vitals').then((webVitals) => {
      try {
        // Core Web Vitals
        if (webVitals.onCLS && typeof webVitals.onCLS === 'function') {
          webVitals.onCLS(sendToAnalytics);
        }
        if (webVitals.onLCP && typeof webVitals.onLCP === 'function') {
          webVitals.onLCP(sendToAnalytics);
        }
        
        // Other important metrics
        if (webVitals.onFCP && typeof webVitals.onFCP === 'function') {
          webVitals.onFCP(sendToAnalytics);
        }
        if (webVitals.onTTFB && typeof webVitals.onTTFB === 'function') {
          webVitals.onTTFB(sendToAnalytics);
        }
        
        // INP (Interaction to Next Paint) - Chrome 96+
        if (webVitals.onINP && typeof webVitals.onINP === 'function') {
          webVitals.onINP(sendToAnalytics);
        }
      } catch (error) {
        console.warn('Web Vitals initialization failed:', error);
      }
    }).catch((error) => {
      console.warn('Web Vitals could not be loaded:', error);
    });
  }, []);

  // Performance observer for custom metrics
  useEffect(() => {
    if (typeof window === 'undefined' || !window.PerformanceObserver) return;

    try {
      // Navigation timing
      const observer = new PerformanceObserver((list) => {
        try {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
          
          // Custom metrics
          const metrics = {
            domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart,
            loadComplete: navEntry.loadEventEnd - navEntry.loadEventStart,
            totalPageLoad: navEntry.loadEventEnd - navEntry.fetchStart,
            dnsLookup: navEntry.domainLookupEnd - navEntry.domainLookupStart,
            tcpConnect: navEntry.connectEnd - navEntry.connectStart,
            serverResponse: navEntry.responseEnd - navEntry.requestStart
          };

              // Send custom metrics to analytics
              Object.entries(metrics).forEach(([name, value]) => {
                if (value > 0) {
                  sendToAnalytics({
                    name: `custom_${name}`,
                    value,
                    rating: value < 1000 ? 'good' : value < 2000 ? 'needs-improvement' : 'poor',
                    delta: 0,
                    id: `${name}_${Date.now()}`
                  });
                }
              });
            }
          }
        } catch (error) {
          console.warn('Navigation timing processing failed:', error);
        }
      });

      observer.observe({ entryTypes: ['navigation'] });

      return () => observer.disconnect();
    } catch (error) {
      console.warn('Performance Observer setup failed:', error);
    }
  }, []);

  // Resource timing for bundle analysis
  useEffect(() => {
    if (typeof window === 'undefined' || !window.PerformanceObserver) return;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          const resourceEntry = entry as PerformanceResourceTiming;
          
          // Track large resources
          if (resourceEntry.transferSize > 100000) { // > 100KB
            sendToAnalytics({
              name: 'large_resource',
              value: resourceEntry.transferSize,
              rating: resourceEntry.transferSize > 500000 ? 'poor' : 'needs-improvement',
              delta: 0,
              id: `resource_${resourceEntry.name}_${Date.now()}`
            });
          }
        }
      }
    });

    observer.observe({ entryTypes: ['resource'] });

    return () => observer.disconnect();
  }, []);
}

// Performance budget checker
export function checkPerformanceBudget() {
  if (typeof window === 'undefined') return;

  const budget = {
    maxBundleSize: 1000000, // 1MB
    maxLCP: 2500, // 2.5s
    maxFID: 100, // 100ms
    maxCLS: 0.1 // 0.1
  };

  // Check current performance
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (navigation) {
    const loadTime = navigation.loadEventEnd - navigation.fetchStart;
    
    if (loadTime > budget.maxLCP) {
      console.warn(`🚨 Performance Budget: Page load time ${loadTime}ms exceeds budget ${budget.maxLCP}ms`);
    }
  }

  // Check resource sizes
  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const totalSize = resources.reduce((sum, resource) => sum + (resource.transferSize || 0), 0);
  
  if (totalSize > budget.maxBundleSize) {
    console.warn(`🚨 Performance Budget: Total resource size ${totalSize} bytes exceeds budget ${budget.maxBundleSize} bytes`);
  }
}